import { APP_TIMEZONE } from '@/constants/timezone';
import { BMT_COURSE_AUDIENCE, BMT_ENROLMENT_STATUS } from '@/constants/bmt';

/**
 * Display helpers for BMT Companion. Pure functions of the API's views, so the
 * screens hold no date or status logic of their own and every branch is testable.
 *
 * Times arrive as UTC instants and are always shown in Singapore time, whatever
 * zone the phone is in: a trainee abroad still has to be in the hall at 7 pm SGT.
 * `date` fields are already Singapore calendar dates (YYYY-MM-DD).
 */

const partsOf = (date, options) =>
  Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', options)
      .formatToParts(date)
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value]),
  );

const isValidDate = date => date instanceof Date && !Number.isNaN(date.getTime());

const parseCalendarDate = value => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  // Noon UTC, formatted in UTC: the calendar date cannot drift a day either way.
  const date = new Date(`${value}T12:00:00Z`);
  return isValidDate(date) ? date : null;
};

/** '2026-11-07' → 'Sat 7 Nov'. */
export function formatDayDate(value) {
  const date = parseCalendarDate(value);
  if (!date) return '';
  const { weekday, day, month } = partsOf(date, {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return `${weekday} ${day} ${month}`;
}

/** '2026-11-05' → { month: 'NOV', day: '5', weekday: 'THU' }, for a calendar tile. */
export function calendarParts(value) {
  const date = parseCalendarDate(value);
  if (!date) return { month: '', day: '', weekday: '' };
  const { weekday, day, month } = partsOf(date, {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return { month: month.toUpperCase(), day, weekday: weekday.toUpperCase() };
}

/** A UTC instant → '11am' or '4.20pm' in Singapore time. */
export function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (!isValidDate(date)) return '';
  const { hour, minute, dayPeriod } = partsOf(date, {
    timeZone: APP_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    // h12, not hour12: some ICU builds read hour12 as h11 and print noon as "0pm".
    hourCycle: 'h12',
  });
  const clock = minute === '00' ? hour : `${hour}.${minute}`;
  return `${clock}${(dayPeriod || '').toLowerCase()}`;
}

/** '7pm – 9.30pm', or just the start when there is no end. */
export function formatTimeRange(startsAt, endsAt) {
  const start = formatTime(startsAt);
  const end = formatTime(endsAt);
  if (start && end) return `${start} – ${end}`;
  return start || end;
}

/** The first and last date of a run of days: 'Sat 7 Nov – Sat 28 Nov'. */
export function formatDateSpan(days = []) {
  const dates = days.map(day => day.date).filter(Boolean);
  if (!dates.length) return '';
  const first = formatDayDate(dates[0]);
  const last = formatDayDate(dates[dates.length - 1]);
  return first === last ? first : `${first} – ${last}`;
}

/**
 * The check-in line for a day. The app never offers self check-in: the only
 * way in is scanning the QR shown in the hall, so `canScan` is just a hint for
 * which button to emphasise.
 */
export function checkinLabel(checkin) {
  const state = checkin?.state || 'none';
  if (state === 'not-yet') {
    const at = formatTime(checkin.opensAt);
    return {
      state,
      text: at ? `Check-in opens at ${at}` : 'Check-in not open yet',
      canScan: false,
    };
  }
  if (state === 'open') return { state, text: 'Check-in open', canScan: true };
  if (state === 'closed') return { state, text: 'Check-in closed', canScan: false };
  return { state: 'none', text: '', canScan: false };
}

/**
 * One status for a whole day: upcoming while any session is still to come,
 * attended when every session is, partial when some were missed, missed when
 * none were attended.
 */
export function dayStatus(day) {
  const sessions = day?.sessions || [];
  if (!sessions.length || sessions.some(s => s.status === 'upcoming')) return 'upcoming';
  const attended = sessions.filter(s => s.status === 'attended').length;
  if (attended === sessions.length) return 'attended';
  return attended ? 'partial' : 'missed';
}

/**
 * The day a trainee should look at next: today's while check-in is not over,
 * otherwise the first one still to come. Null once every day has passed.
 */
export function nextDay(days = [], serverTime) {
  const now = serverTime ? new Date(serverTime) : new Date();
  return (
    days.find(day => {
      if (day.checkin?.state === 'open' || day.checkin?.state === 'not-yet') return true;
      if (day.checkin?.state === 'closed') return false;
      const end = day.endsAt ? new Date(day.endsAt) : null;
      if (end && isValidDate(end)) return end > now;
      return dayStatus(day) === 'upcoming';
    }) || null
  );
}

/** The Singapore calendar date of a UTC instant (or now), as YYYY-MM-DD. */
export function sgCalendarDate(value) {
  const date = value ? new Date(value) : new Date();
  if (!isValidDate(date)) return '';
  const { year, month, day } = partsOf(date, {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return `${year}-${month}-${day}`;
}

/** A day's topic titles, joined: 'Prayer · Serving in Church'. */
export const topicsText = day => (day?.sessions || []).map(s => s.title).join(' · ');

/** Where a day is: its venue, or 'Online' for a Zoom day. */
export const dayPlace = day => (day?.online ? 'Online' : day?.venue || '');

/** Time range and place on one line: '7pm – 9.30pm · Hall 2'. */
export const dayTimeVenue = day =>
  [formatTimeRange(day?.startsAt, day?.endsAt), dayPlace(day)].filter(Boolean).join(' · ');

/** Today's day (Singapore date, by the server's clock) while its check-in is open, or null. */
export function openCheckinDay(me) {
  if (!['pending', 'approved'].includes(bmtPhase(me))) return null;
  const today = sgCalendarDate(me.serverTime);
  return (me.days || []).find(day => day.date === today && day.checkin?.state === 'open') || null;
}

/**
 * What the /bmt/check-in screen says for a scan response kept in the store
 * (`bmt.lastCheckIn`: `{ status, code, display, checkIn, opensAt }`). A repeat
 * scan reads the same as the first: the attendance is recorded either way.
 */
export function checkInResultModel(result) {
  const checkIn = result?.checkIn || {};
  if (result?.status === 'success') {
    if (checkIn.enrolmentStatus === BMT_ENROLMENT_STATUS.WITHDRAWN) return withdrawnModel();
    const count = checkIn.sessions?.length || 0;
    return {
      kind: 'checked-in',
      title: "You're checked in",
      subtitle: checkIn.date ? `for ${formatDayDate(checkIn.date)}` : '',
      message:
        count === 1
          ? "Today's attendance is recorded."
          : "Attendance for all of today's sessions is recorded.",
      pending: checkIn.enrolmentStatus === BMT_ENROLMENT_STATUS.PENDING,
    };
  }
  if (result?.code === 'CHECKIN_NOT_OPEN') {
    const opens = formatTime(result.opensAt);
    return {
      kind: 'not-open',
      title: opens ? `Check-in opens at ${opens}` : "Check-in isn't open yet",
      message: opens ? `Nothing's saved yet. Scan again from ${opens}.` : "Nothing's saved yet.",
    };
  }
  if (result?.code === 'CHECKIN_CLOSED') {
    return {
      kind: 'closed',
      title: 'Check-in has closed',
      message: 'Please speak to the BMT team.',
    };
  }
  if (result?.code === 'SESSION_NOT_AVAILABLE' || result?.code === 'INVALID_QR') {
    return {
      kind: 'unavailable',
      title: "This session isn't open for check-in",
      message: "Check that you've scanned the correct QR code.",
    };
  }
  if (!result?.code && !result?.display) {
    // No answer at all: the network. The scan may still have saved, and a retry is safe.
    return {
      kind: 'offline',
      title: "Couldn't check you in",
      message: "Check your connection and try again. If you're already checked in, you'll see it.",
      retry: true,
    };
  }
  return {
    kind: 'error',
    title: "That didn't work",
    message: result?.display?.message || 'Please speak to the BMT team.',
  };
}

/** E4: someone the BMT team withdrew. Never a reason. */
export function withdrawnModel() {
  return {
    kind: 'withdrawn',
    title: 'Please speak to the BMT team',
    message: "Your BMT place isn't active right now.",
  };
}

const isEnrolled = me =>
  Boolean(me?.enrolment) && me.enrolment.status !== BMT_ENROLMENT_STATUS.WITHDRAWN;

/**
 * Which face the BMT hub shows: 'pending', 'approved', 'withdrawn' (the BMT team
 * took them off; they speak to the team, never re-sign-up here), 'signup' (not
 * enrolled, with an intake open), or 'none' (nothing to offer).
 */
export function bmtPhase(me) {
  if (!me) return 'none';
  if (isEnrolled(me)) {
    return me.enrolment.status === BMT_ENROLMENT_STATUS.APPROVED ? 'approved' : 'pending';
  }
  if (me.enrolment?.status === BMT_ENROLMENT_STATUS.WITHDRAWN) return 'withdrawn';
  return me.openIntakes?.length ? 'signup' : 'none';
}

/** 'For Youth & JC' under 'BMT Youth', from the course label. */
export const courseAudience = label => BMT_COURSE_AUDIENCE[label] || '';

/** The hero card on the hub: today's day, else the next to come; null once all are past. */
function heroDay(days, today) {
  return days.find(day => day.date === today) || days.find(day => day.date > today) || null;
}

/** 'attended' when any session that day was checked in to (one scan covers the day). */
const checkedInOn = day => (day?.sessions || []).some(s => s.status === 'attended');

/** The hero card's state: what the day is doing, from the trainee's side. */
function heroState(day, isToday) {
  if (day.online) return 'online';
  if (isToday && checkedInOn(day)) return 'checked-in';
  return day.checkin?.state || 'none';
}

/** The line under the hero card's date and time. */
function heroInfo(state, day, pending) {
  if (state === 'not-yet') {
    if (pending) {
      return `Going on ${formatDayDate(day.date)} anyway? Come along and scan the QR code at the venue to check in.`;
    }
    const opens = formatTime(day.checkin?.opensAt);
    return opens
      ? `Check-in opens at ${opens} on the day. Scan the QR code at the venue.`
      : 'Scan the QR code at the venue to check in.';
  }
  if (state === 'checked-in') {
    const count = day.sessions?.length || 0;
    return count === 1
      ? "Today's attendance is recorded."
      : `Attendance for all ${count} sessions today is recorded.`;
  }
  if (state === 'open') return pending ? 'You can check in while you wait to be confirmed.' : '';
  return (
    {
      closed: 'Check-in has closed. Please speak to the BMT team.',
      online: 'This session is on Zoom.',
    }[state] || ''
  );
}

const HERO_PILL = {
  'not-yet': { tone: 'muted', text: 'Check-in not open yet' },
  open: { tone: 'live', text: 'Check-in open' },
  'checked-in': { tone: 'success', text: 'Checked in' },
  closed: { tone: 'muted', text: 'Check-in closed' },
  online: { tone: 'muted', text: 'Zoom' },
};

/** The hero card for today's day, else the next to come. Pending sees no "not open" pill. */
function heroModel(day, isToday, pending) {
  const state = heroState(day, isToday);
  return {
    label: isToday ? 'Today' : 'Next',
    date: formatDayDate(day.date),
    timeVenue: dayTimeVenue(day),
    state,
    pill: pending && state === 'not-yet' ? null : HERO_PILL[state] || null,
    info: heroInfo(state, day, pending),
    canScan: state === 'open',
    meetingUrl: day.online && isToday ? day.meetingUrl || null : null,
  };
}

const LOCKED_TILE_TEXT = 'Opens once the BMT team confirms your place';

/** The three tiles. Pending trainees see Resources and Tasks locked, saying when they open. */
function hubTiles(locked, todayResources, today, homework) {
  const done = homework ? homework.filter(item => item.submitted).length : 0;
  let resources = 'Videos and notes for each session';
  if (todayResources) resources = `${formatDayDate(today)} resources are open`;
  return [
    { key: 'schedule', title: 'Schedule', subtitle: '', locked: false },
    {
      key: 'resources',
      title: 'Resources',
      subtitle: locked ? LOCKED_TILE_TEXT : resources,
      isNew: !locked && Boolean(todayResources),
      locked,
    },
    {
      key: 'tasks',
      title: 'Tasks',
      subtitle: locked ? LOCKED_TILE_TEXT : homework ? `${done} of ${homework.length} done` : '',
      locked,
    },
  ];
}

/**
 * Everything the BMT hub (L1–L7) shows for a pending or approved trainee: the
 * hero card, the last in-person session behind them, and the three tiles.
 * `homework` is the /homework list once loaded (approved only), else null.
 */
export function hubModel(me, { homework = null, resources = null } = {}) {
  const phase = bmtPhase(me);
  const days = me?.days || [];
  const today = sgCalendarDate(me?.serverTime);
  const day = heroDay(days, today);
  const past = days.filter(d => d.date < today && !d.online);
  const lastDay = past[past.length - 1];
  const todayResources = (resources?.days || []).find(d => d.date === today && d.unlocked);
  return {
    phase,
    label: me?.enrolment?.label || 'BMT',
    audience: courseAudience(me?.enrolment?.label),
    hero: day ? heroModel(day, day.date === today, phase === 'pending') : null,
    allDone: !day && days.length > 0,
    last: lastDay ? { date: formatDayDate(lastDay.date), attended: checkedInOn(lastDay) } : null,
    tiles: hubTiles(phase !== 'approved', todayResources, today, homework),
  };
}

/**
 * The Schedule page: one card per date. Upcoming in-person dates say when
 * check-in opens; past dates and Zoom dates do not. No attendance here: the
 * hub already shows it.
 */
export function scheduleModel(me) {
  const today = sgCalendarDate(me?.serverTime);
  return (me?.days || []).map(day => ({
    date: day.date,
    title: formatDayDate(day.date),
    calendar: calendarParts(day.date),
    time: formatTimeRange(day.startsAt, day.endsAt),
    place: dayPlace(day),
    online: Boolean(day.online),
    meetingUrl: day.meetingUrl || null,
    past: day.date < today,
    checkinText:
      !day.online && day.checkin?.state === 'not-yet' && day.checkin.opensAt
        ? `Check-in opens ${formatTime(day.checkin.opensAt)}`
        : '',
    sessions: (day.sessions || []).map(session => ({
      id: session.id,
      title: session.title,
      note: session.note || '',
      start: formatTime(session.startsAt),
      end: formatTime(session.endsAt),
    })),
  }));
}

/** What a locked date on the Resources page says about when it opens. */
export function resourceLockText(day) {
  const count = day.sessionCount || 0;
  const sessions = `${count} ${count === 1 ? 'session' : 'sessions'}`;
  if (day.online) {
    const at = formatTime(day.opensAt);
    return `${sessions} · Opens ${formatDayDate(day.date)}${at ? `, ${at}` : ''}`;
  }
  return `${sessions} · Opens when you check in on ${formatDayDate(day.date)}`;
}

/** "From i3 · Thu 5 Nov": which session brings a task in. */
export const taskSource = item =>
  item?.session
    ? `From ${[item.session.title, formatDayDate(item.session.date)].filter(Boolean).join(' · ')}`
    : '';

/** The earliest first date across the open intakes, for "BMT starts …". */
export function earliestStart(openIntakes = []) {
  const firsts = openIntakes.map(intake => intake.days?.[0]?.date).filter(Boolean);
  return firsts.length ? firsts.sort()[0] : null;
}

/**
 * Everything the home card says, or null when it should not render.
 * Trainee copy says "BMT" and never an intake or batch number.
 */
export function homeCardModel(me) {
  const phase = bmtPhase(me);
  if (phase === 'signup') {
    const start = earliestStart(me.openIntakes);
    return {
      phase,
      title: start ? `BMT starts ${formatDayDate(start)}` : 'BMT sign-up is open',
      detail: 'Sign up',
    };
  }
  if (phase === 'pending') {
    return { phase, title: 'BMT', detail: 'Waiting for the BMT team to confirm' };
  }
  if (phase === 'approved') {
    const day = nextDay(me.days, me.serverTime);
    if (!day) return { phase, title: 'BMT', detail: 'All your BMT days are done' };
    const time = formatTimeRange(day.startsAt, day.endsAt);
    return {
      phase,
      title: `BMT · ${formatDayDate(day.date)}`,
      detail: [time, day.venue].filter(Boolean).join(' · '),
      checkin: checkinLabel(day.checkin),
    };
  }
  return null;
}

const ERROR_MESSAGES = {
  CG_NOTE_REQUIRED: 'Let us know whether you are in a Connect Group first.',
  INTAKE_NOT_OPEN: 'Sign-up for that BMT has just closed. The list now shows what is open.',
  ALREADY_ENROLLED: 'You are already signed up for BMT.',
  NOT_ENROLLED: 'Sign up for BMT to see your tasks.',
  NOT_APPROVED: 'Tasks open once the BMT team confirms your place.',
  HOMEWORK_NOT_FOUND: 'That task is no longer available.',
  TEXT_REQUIRED: 'Write your answer before submitting.',
};

/** User-facing copy for an API error body `{ code, message }`, or a network failure. */
export function bmtErrorMessage(error) {
  const code = error?.code || error?.response?.data?.code;
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  return 'Something went wrong. Please try again.';
}
